import React from 'react';
import { useDispatch } from 'react-redux';

import { getSyncPairsAriba } from '~/api/evisync';
import { getProcurementIntegrations } from '~/common/constants/integrations';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Text } from '~/eds/src/components/Text/Text';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { useAsync } from '~/hooks/useAsync';
import { useIntl } from 'react-intl';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import { createSyncPairData } from '~/utils/integrations';

import { getSyncPairCardStatus } from '../util';
import IntegrationCardList from './IntegrationCardList';

function ProcurementIntegrationCardList({ isCentered = false }) {
  const { params } = useRouting();
  const intl = useIntl();
  const dispatch = useDispatch();

  const { clientId } = params;

  const { response: aribaSyncPairs } = useAsync(
    getSyncPairsAriba,
    { clientId },
    {
      condition: true,
      errorToastMessage: intl.formatMessage({
        id: 'ProcurementIntegrationCardList.unableToFetchSyncPairsError',
        defaultMessage: 'Unable to fetch sync pairs, please try again.',
        description: 'Error message when fetching procurement sync pairs',
      }),
      successHandler: (data) =>
        dispatch(slices.integrations.actions.setSyncPairs(data)),
    },
  );

  const renderSyncPairCardStatus = ({ color, icon, count }) => {
    if (!count || count <= 0) {
      return <Icon color={color} icon={icon} />;
    }

    return (
      <FlexLayout alignItems="center" space={2}>
        <Icon color={color} icon={icon} />
        <Text color={color} variant="tiny-bold">
          {count}
        </Text>
      </FlexLayout>
    );
  };

  let integrationCards = Object.values(getProcurementIntegrations()).map(
    (integration) => ({
      ...integration,
      status: renderSyncPairCardStatus(
        getSyncPairCardStatus(integration.provider, aribaSyncPairs),
      ),
      description: intl.formatMessage(
        {
          id: `ProcurementIntegrationCardList.providerDescription`,
          defaultMessage:
            'Synchronize documents stored on {integration} with Evisort.',
          description:
            'Description for procurement integration card. Example: integration = "Salesforce"',
        },
        {
          integration: integration.name,
        },
      ),
      action: {
        text: intl.formatMessage({
          id: 'ProcurementIntegrationCardList.addSyncPair',
          defaultMessage: 'Add Sync Pair',
          description:
            'Button text to add a sync pair for procurement integrations | maxLength=60',
        }),
        onClick: () => {
          const syncPairData = createSyncPairData(integration);
          dispatch(slices.integrations.actions.setActiveSyncPair(syncPairData));
          dispatch(
            slices.integrations.actions.setManageSyncPairStage(
              ManageSyncPairStageType.MultiAPIs,
            ),
          );
        },
      },
    }),
  );

  return (
    <IntegrationCardList
      isCentered={isCentered}
      integrationCards={integrationCards}
    />
  );
}

export default ProcurementIntegrationCardList;
